import { useState } from "react";
import { Link } from "wouter";
import { Users, Building2, ClipboardList, Mail, ChevronDown, ChevronUp, Activity, BarChart3, Clock } from "lucide-react";
import { formatSectorLabel } from "@/lib/sector-labels";
import type { AdminStats } from "../AdminDashboard";

interface DerivedStats {
  overallAvgCompleteness: number;
  mostActiveSector: { sector: string; count: number } | null;
  lastActivityAt: string | null;
}

interface StatCardsProps {
  stats: AdminStats;
  derivedStats: DerivedStats;
}

function StatCard({
  title,
  value,
  icon: Icon,
  subtitle,
  href,
}: {
  title: string;
  value: number;
  icon: React.ElementType;
  subtitle?: string;
  href: string;
}) {
  return (
    <Link href={href}>
      <div className="p-5 bg-card rounded-3xl shadow-[0px_4px_6px_-4px_rgba(226,232,240,0.50),0px_10px_15px_-3px_rgba(226,232,240,0.50)] outline outline-1 outline-offset-[-1px] outline-border hover:outline-primary/40 transition-colors cursor-pointer overflow-hidden">
        <div className="flex items-center justify-between mb-3">
          <span className="text-sm font-medium text-slate-500">{title}</span>
          <div className="w-[35px] h-[35px] bg-primary/10 rounded-xl flex items-center justify-center">
            <Icon className="h-4 w-4 text-primary" />
          </div>
        </div>
        <div className="text-3xl font-extrabold text-primary">{value}</div>
        {subtitle && <p className="text-xs text-slate-400 mt-1">{subtitle}</p>}
      </div>
    </Link>
  );
}

interface InfoCardProps {
  title: string;
  value: string;
  subtitle?: string;
  icon: React.ElementType;
}

function InfoCard({ title, value, subtitle, icon: Icon }: InfoCardProps) {
  return (
    <div className="p-5 bg-card rounded-3xl shadow-[0px_4px_6px_-4px_rgba(226,232,240,0.50),0px_10px_15px_-3px_rgba(226,232,240,0.50)] outline outline-1 outline-offset-[-1px] outline-border overflow-hidden">
      <div className="flex items-center justify-between mb-3">
        <span className="text-sm font-medium text-slate-500">{title}</span>
        <div className="w-[35px] h-[35px] bg-primary/10 rounded-xl flex items-center justify-center">
          <Icon className="h-4 w-4 text-primary" />
        </div>
      </div>
      <div className="text-3xl font-extrabold text-primary">{value}</div>
      {subtitle && <p className="text-xs text-slate-400 mt-1">{subtitle}</p>}
    </div>
  );
}

export function StatCards({ stats, derivedStats }: StatCardsProps) {
  const [expanded, setExpanded] = useState(true);

  const getStatusCount = (status: string) =>
    stats.assessmentsByStatus.find((s) => s.status === status)?.count || 0;

  const { overallAvgCompleteness, mostActiveSector, lastActivityAt } = derivedStats;

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-lg font-semibold text-slate-700">{"Key Metrics"}</h2>
        <button
          onClick={() => setExpanded((v) => !v)}
          className="p-1 rounded-lg hover:bg-slate-100 transition-colors text-slate-500"
          aria-label={expanded ? "Collapse" : "Expand"}
        >
          {expanded ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
        </button>
      </div>

      {expanded && (
        <div className="space-y-4">
          {/* Row 1 — existing stat cards */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            <StatCard
              title={"Total Users"}
              value={stats.totalUsers}
              icon={Users}
              subtitle={`${stats.activeUsers} active · ${stats.inactiveUsers} inactive`}
              href="/admin/users"
            />
            <StatCard
              title={"Workspaces"}
              value={stats.totalWorkspaces}
              icon={Building2}
              subtitle={`${stats.totalWorkspaces - stats.disabledWorkspaces} active · ${stats.disabledWorkspaces} disabled`}
              href="/admin/workspaces"
            />
            <StatCard
              title={"Assessments"}
              value={stats.totalAssessments}
              icon={ClipboardList}
              subtitle={`${getStatusCount("draft")} draft · ${getStatusCount("complete")} complete · ${getStatusCount("finalized")} finalized`}
              href="/admin/assessments"
            />
            <StatCard
              title={"Invitations"}
              value={stats.totalInvitations}
              icon={Mail}
              subtitle={`${stats.pendingInvitations} pending`}
              href="/admin/invitations"
            />
          </div>

          {/* Row 2 — derived info cards */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <InfoCard
              title={"Overall Avg Completeness"}
              value={`${overallAvgCompleteness}%`}
              subtitle={"across all assessments"}
              icon={Activity}
            />
            <InfoCard
              title={"Most Active Sector"}
              value={mostActiveSector ? formatSectorLabel(mostActiveSector.sector) : "—"}
              subtitle={
                mostActiveSector
                  ? `${mostActiveSector.count} assessments`
                  : undefined
              }
              icon={BarChart3}
            />
            <InfoCard
              title={"Last Assessment Activity"}
              value={lastActivityAt ? (() => {
                const diffMs = Date.now() - new Date(lastActivityAt).getTime();
                const diffMins = Math.floor(diffMs / 60000);
                if (diffMins < 1) return "just now";
                if (diffMins < 60) return `${diffMins}m ago`;
                const diffHours = Math.floor(diffMins / 60);
                if (diffHours < 24) return `${diffHours}h ago`;
                return `${Math.floor(diffHours / 24)}d ago`;
              })() : "—"}
              subtitle={lastActivityAt ? "last update" : undefined}
              icon={Clock}
            />
          </div>
        </div>
      )}
    </div>
  );
}
